import { Spinner } from '@common/components/Spinner';
import ImgixWrapper from '@common/components/ImgixWrapper';
import Markdown from '@common/components/Markdown';
import SectionContainer from '@common/components/SectionContainer';
import { Heading4 } from '@common/components/Typography';
import { DatePicker } from '@features/JourneyPicker';
import { Button } from '@common/components';
import { DiscountBanner, FromPrice } from '@common/components/Price';
import { Link } from 'shared-routing';
import { Routes } from '@common/constants';
import { parseDateToString } from '@utils/helpers';
import React, { FC, useState } from 'react';
import moment from 'moment';
import { useTranslations } from '@lang';
import {
  Column,
  MobileColumn,
  NameColumn,
  SpaDaysCategoryWrapper,
  SpaDaysListing,
  SpaDaysListingWrapper,
  SpaDaysPageDescription,
  SpaDaysPagePreview,
  SpaDaysRow,
} from './SpaDaysCategory.styles';
import { DispatchProps, StateProps } from './SpaDaysCategory.types';
import { markUnavailableSpaPackages } from './utils';
import { useGetAllSpaPackages } from '@features/spa/hooks/useGetAllSpaPackages';
import { useGetAvailableSpaPackages } from '@features/spa/hooks/useGetAvailableSpaPackages';
import { useGetSpaCategories } from '@features/spa/hooks/useGetSpaCategories';
import { useGetSpaDaysPage } from '@features/spa/hooks/useGetSpaDaysPage';
import { SpaPackageListingType } from 'shared-types/SpaPackage';
import { Category } from 'shared-types';

export type SpaDaysCategoryProps = {
  language: string;
  category: Category;
} & StateProps &
  DispatchProps;

export const SpaDaysCategory: FC<SpaDaysCategoryProps> = ({
  property,
  journeyParams,
  changeJourneyParam,
  language,
  category,
}) => {
  const { t } = useTranslations();
  const [date, setDate] = useState(journeyParams.spaDate);

  const { spaDate } = journeyParams;

  const getSpaDateString = () => {
    if (!spaDate) return moment().format('YYYY-MM-DD');
    return parseDateToString(spaDate);
  };
  const { data: spaPackagesAll = [] } = useGetAllSpaPackages(property.id, language);

  const { data: spaDaysPage = { title: '', content: '', heroBackgroundImage: { url: '' } } } =
    useGetSpaDaysPage(property.id, language);

  const {
    data: spaPackages = [],
    isLoading,
    isFetching,
  } = useGetAvailableSpaPackages(
    property.id,
    { category: Number(category.id), date: getSpaDateString() },
    language
  );

  const { data: spaPackagesCategories = [] } = useGetSpaCategories(
    property.id,
    'packages',
    language
  );

  const handleArrivalDateChange = () => {
    changeJourneyParam('fromDate', moment(date).format('YYYY-MM-DD'));
    changeJourneyParam('spaDate', moment(date).format('YYYY-MM-DD'));
  };

  const spaPackagesAllFiltered = spaPackagesAll.filter((spaPackage: SpaPackageListingType) => {
    const spaPackageCategories = spaPackage.categories.map((cat) => cat.id);
    return spaPackageCategories.includes(category.id);
  });
  const currentCategory = spaPackagesCategories?.find(({ id }) => id === category.id) || null;
  const markedSpaPackages = markUnavailableSpaPackages(spaPackagesAllFiltered, spaPackages);

  return (
    currentCategory && (
      <>
        <SpaDaysCategoryWrapper>
          <Spinner isActive={isFetching || isLoading} showDelayMessage />
          <SectionContainer>
            <DatePicker
              fromDate={moment(journeyParams.fromDate)}
              onDateChange={setDate}
              handleUpdate={handleArrivalDateChange}
            />
            <Heading4 tag="h1">{currentCategory.name}</Heading4>

            <SpaDaysListingWrapper>
              <SpaDaysListing>
                {markedSpaPackages
                  .sort((a, b) => a.displayOrder - b.displayOrder)
                  .map((spaPackage) => {
                    const isAvailable = !spaPackage.unAvailable;
                    return (
                      <SpaDaysRow key={spaPackage.id} isAvailable={isAvailable}>
                        <MobileColumn>
                          <NameColumn>{spaPackage.name}</NameColumn>
                          <Column>
                            <DiscountBanner discount={spaPackage.discount} isPlaced={false} />
                            <FromPrice {...spaPackage} />
                          </Column>
                        </MobileColumn>
                        <Column>
                          <Link route={Routes.SPA_DAY_DETAIL} params={{ id: spaPackage.id }}>
                            <Button isPrimary={isAvailable} isSmall>
                              {isAvailable ? t('common.moreDetails') : t('common.findOtherDates')}
                            </Button>
                          </Link>
                        </Column>
                      </SpaDaysRow>
                    );
                  })}
              </SpaDaysListing>
              <SpaDaysPagePreview>
                {(category.image?.url || spaDaysPage.heroBackgroundImage) && (
                  <ImgixWrapper
                    src={category.image?.url || spaDaysPage.heroBackgroundImage.url}
                    width={426}
                    height={284}
                    alt={currentCategory.name}
                  />
                )}
                <SpaDaysPageDescription>
                  <Markdown text={category.description || spaDaysPage.content} />
                </SpaDaysPageDescription>
              </SpaDaysPagePreview>
            </SpaDaysListingWrapper>
          </SectionContainer>
        </SpaDaysCategoryWrapper>
      </>
    )
  );
};
